import {
  IFarmResource,
  ILineResource,
  IMusselLineResource,
  IOysterLineResource,
  ISeaweedLineResource,
} from '../../../entities/farms.entities';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate, transUnit } from '../../../lib/lang.helper';
import Dropdown from '../Dropdown';
import Input from '../input/Input';
import { Table } from 'antd';
import {
  selectAllTags,
  SelectAssessFields,
  SelectIsEditable,
} from '../../../store/extra/extra.selector';
import { getLineColumnLabel } from '../../user-settings/LineColumnModifyModal';
import { sendSingleRequest } from '../../../apis';
import { loadFarmsData } from '../../../store/farms/farms.actions';
import { showFeedback } from '../../../store/ui/ui.actions';
import LineLocationModal from '../modal/LineLocationModal';
import LinesFilterView, { checkLineFilter } from '../../lines/LinesFilterView';
import LineFormModal from '../../farm-modals/LineFormModal';
import {
  selectAccount,
  selectIsAdmin,
  selectProfile,
} from '../../../store/auth/auth.selector';
import useMenuHandler from '../../../lib/useMenuHandler';
import {
  amountDays,
  calcDiffDays,
  compareString,
  defaultDateFormat,
  displayValueUnit,
  toCapitalCase,
} from '../../../lib/common.helpers';
import {
  calcLineLastSize,
  getEstHarvestDate,
  getLineStatus,
} from '../../../lib/farm.helpers';
import Subtitle from '../Subtitle';
import { customSpecialValue } from '../../../entities/util-functions';
import { SeedInventoriesLabel } from '../../CommonViews';
import PhotosViewButton from '../../lines/PhotosViewButton';
import DotsMenu, { IMenu } from '../dropdown-menu/DotsMenu';
import { CROP_COLORS } from '../../../lib/constant';

const getLengthSortValue = (line: ILineResource) => {
  if (line.crop_type === 'MUSSEL') {
    return (
      (line as IMusselLineResource).growing_cycle?.current_line_length ?? 0
    );
  }
  if (line.crop_type === 'OYSTER') {
    return (
      (line as IOysterLineResource).growing_cycle?.current_basket_count ?? 0
    );
  }
  if (line.crop_type === 'SEAWEED') {
    return (
      (line as ISeaweedLineResource).growing_cycle?.total_seeded_tank_area ?? 0
    );
  }
  return 0;
};

const renderLength = (line: ILineResource, unitType: any) => {
  if (line.crop_type === 'MUSSEL') {
    const ml = line as IMusselLineResource;
    return ml.growing_cycle ? (
      <span>
        {displayValueUnit(
          ml.growing_cycle.current_line_length,
          unitType,
          ml.line_type === 'LONGLINE' ? 'distance_m' : 'droppers',
        )}
      </span>
    ) : (
      <span>{'-'}</span>
    );
  }
  if (line.crop_type === 'OYSTER') {
    const ol = line as IOysterLineResource;
    return ol.growing_cycle ? (
      <span>
        {`${ol.growing_cycle?.current_basket_count} ${ol.farming_method ?? ''}`}
      </span>
    ) : (
      <span>{'-'}</span>
    );
  }
  if (line.crop_type === 'SEAWEED') {
    const sl = line as ISeaweedLineResource;
    return sl.growing_cycle ? (
      <span>
        {displayValueUnit(
          sl.growing_cycle.total_seeded_tank_area,
          unitType,
          'area_sqm',
        )}
      </span>
    ) : (
      <span>{'-'}</span>
    );
  }
  return <span>{'-'}</span>;
};

interface TableProps {
  linesData: ILineResource[];
  isChild?: boolean;
  farmData: IFarmResource;
  onEditLineLocation: (line: ILineResource) => void;
}

const UnitedLinesTable = ({
  farmData,
  linesData,
  isChild,
  onEditLineLocation,
}: TableProps) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);
  const profile = useSelector(selectProfile);
  const isAdmin = useSelector(selectIsAdmin);
  const customFields = SelectAssessFields(farmData.id);
  const isEditable = SelectIsEditable(farmData.id);
  const accountSetting = useSelector(selectAccount);

  const hiddenColumns =
    farmData.structure === 'RAFT'
      ? accountSetting?.raft_hidden_columns
      : accountSetting?.line_hidden_columns;
  const columnsOrder =
    farmData.structure === 'RAFT'
      ? accountSetting?.raft_columns_order
      : accountSetting?.line_columns_order;

  const { redirectToLine } = useMenuHandler();

  const [editLine, setEditLine] = useState<ILineResource>();

  const archiveLine = async (line: ILineResource) => {
    if (
      !window.confirm(
        translate(lang, 'Are you sure you want to archive this line?'),
      )
    )
      return;
    const response = await sendSingleRequest(
      {},
      'POST',
      `api/farm/line/archive-line/${line.id}`,
      true,
    );
    if (response.status) {
      await dispatch(loadFarmsData());
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, 'Line archived successfully'),
        }),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, response.data?.message ?? 'Unknown error'),
        }),
      );
    }
  };
  const deleteLine = async (line: ILineResource) => {
    if (!window.confirm('Are you sure you want to delete this line?')) return;
    const response = await sendSingleRequest(
      {},
      'DELETE',
      `api/farm/line/lines/${line.id}`,
      true,
    );
    if (response.status) {
      await dispatch(loadFarmsData());
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: 'Line deleted successfully',
        }),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, response.data?.message ?? 'Unknown error'),
        }),
      );
    }
  };

  const changeTable = async (pagination: any, filter: any, sorter: any) => {
    const columnKey: any = sorter?.columnKey;
    const orders: any = sorter?.order;
    const data = { columnKey, orders, farmId: farmData.id };
    await sendSingleRequest(data, 'POST', 'api/farm/line-sorting', true);
  };

  const tempColumns = [
    {
      title: transUnit(lang, 'line', farmData.structure),
      key: 'line_name',
      defaultSortOrder: null,
      sorter: (a: ILineResource, b: ILineResource) =>
        compareString(a.line_name, b.line_name),
      render: (x: ILineResource) => (
        <div
          style={{
            textAlign: 'center',
            borderBottom: `5px solid ${CROP_COLORS[x.crop_type]}`,
          }}
        >
          {x.line_name}
        </div>
      ),
    },
    {
      title: getLineColumnLabel('crop_type', lang),
      key: 'crop_type',
      defaultSortOrder: null,
      sorter: (a: ILineResource, b: ILineResource) =>
        compareString(a.crop_type, b.crop_type),
      render: (x: ILineResource) => (
        <span style={{ color: CROP_COLORS[x.crop_type] }}>
          {translate(lang, toCapitalCase(x.crop_type))}
        </span>
      ),
    },
    {
      title: getLineColumnLabel('length', lang),
      key: 'length',
      defaultSortOrder: null,
      sorter: (a: ILineResource, b: ILineResource) =>
        getLengthSortValue(a) - getLengthSortValue(b),
      render: (line: ILineResource) =>
        renderLength(line, accountSetting?.display_unit),
    },
    {
      title: getLineColumnLabel('date_seeded', lang),
      key: 'date_seeded',
      defaultSortOrder: null,
      sorter: (a: ILineResource, b: ILineResource) => {
        if (a.growing_cycle) {
          return (
            Number(a.growing_cycle.main_seed.planned_date_seed) -
            Number(b.growing_cycle?.main_seed.planned_date_seed)
          );
        } else {
          return (a.line_idle ?? 0) - (b.line_idle ?? 0);
        }
      },
      render: (line: ILineResource) => (
        <>
          {line.growing_cycle ? (
            defaultDateFormat(line.growing_cycle.main_seed.planned_date_seed)
          ) : (
            <div className='d-flex flex-wrap'>
              <span className='mnw-74 pr-4'>
                {translate(lang, 'Line empty for')}
              </span>
              <Subtitle size={5} color='black' align='left' fontWeight={600}>
                {amountDays(line.line_idle, lang)}
              </Subtitle>
            </div>
          )}
        </>
      ),
    },
    {
      title: getLineColumnLabel('planned_date', lang),
      key: 'planned_date',
      defaultSortOrder: null,
      sorter: (a: ILineResource, b: ILineResource) =>
        (getEstHarvestDate(a) ?? 0) - (getEstHarvestDate(b) ?? 0),
      render: (line: ILineResource) => {
        return <span>{defaultDateFormat(getEstHarvestDate(line))}</span>;
      },
    },
    {
      title: getLineColumnLabel('seed_type', lang),
      defaultSortOrder: null,
      key: 'seed_type',
      render: (line: ILineResource) => (
        <span>
          {line.growing_cycle?.main_seed.spat_storage?.seed_type ?? '-'}
        </span>
      ),
    },
    {
      title: getLineColumnLabel('last_assessment_date', lang),
      key: 'last_assessment_date',
      defaultSortOrder: null,
      render: (line: ILineResource) => {
        const x = line.growing_cycle?.last_assessment?.assessment_date;
        const d =
          getLineStatus(line, accountSetting?.line_assess_expire_days) ===
          'REQUIRE_ASSESSMENT';
        return (
          <span className={d ? 'color-red' : ''}>
            {defaultDateFormat(x, '-')}
          </span>
        );
      },
    },
    {
      title: getLineColumnLabel('avg_size', lang),
      key: 'avg_size',
      defaultSortOrder: null,
      sorter: (a: ILineResource, b: ILineResource) =>
        (calcLineLastSize(a)?.avg ?? 0) - (calcLineLastSize(b)?.avg ?? 0),
      render: (line: ILineResource) => {
        const color = customSpecialValue(
          customFields,
          line.growing_cycle?.last_assessment?.custom_values,
          'Color',
        );
        return line.growing_cycle ? (
          <span
            className={
              color === 'good'
                ? 'color-green-1'
                : color === 'fair'
                  ? 'color-red-1'
                  : ''
            }
          >
            {displayValueUnit(
              calcLineLastSize(line)?.avg,
              accountSetting?.display_unit,
              'size_mm',
              '-',
            )}
          </span>
        ) : (
          ''
        );
      },
    },
    {
      title: transUnit(lang, 'line_group', farmData.structure),
      key: 'line_group',
      sorter: (a: ILineResource, b: ILineResource) =>
        compareString(
          (a as IMusselLineResource).line_group?.name ?? '',
          (b as IMusselLineResource).line_group?.name ?? '',
        ),
      render: (d: ILineResource) => (
        <span>{(d as IMusselLineResource).line_group?.name ?? ''}</span>
      ),
    },
    {
      title: getLineColumnLabel('spat_storage', lang),
      key: 'spat_storage',
      render: (d: ILineResource) => (
        <span>{d.growing_cycle?.main_seed.spat_storage?.source ?? '-'}</span>
      ),
    },
    {
      title: getLineColumnLabel('days_growth', lang),
      key: 'days_growth',
      render: (d: ILineResource) => (
        <span>
          {d.growing_cycle
            ? amountDays(
                calcDiffDays(d.growing_cycle.main_seed.planned_date_seed),
                lang,
                true,
              )
            : '-'}
        </span>
      ),
      sorter: (a: ILineResource, b: ILineResource) => {
        if (a.growing_cycle) {
          return b.growing_cycle
            ? a.growing_cycle.main_seed.planned_date_seed -
                b.growing_cycle.main_seed.planned_date_seed
            : -1;
        } else {
          return b.growing_cycle ? 1 : (a.line_idle ?? 0) - (b.line_idle ?? 0);
        }
      },
    },
    {
      title: getLineColumnLabel('inventory', lang),
      key: 'inventory',
      render: (d: ILineResource) => {
        const musselLine = d as IMusselLineResource;
        return (
          <div>
            {musselLine.growing_cycle?.inventories_sum
              ? SeedInventoriesLabel(musselLine.growing_cycle.inventories_sum)
              : '-'}
          </div>
        );
      },
      sorter: (a: ILineResource, b: ILineResource) => {
        const musselA = a as IMusselLineResource;
        const musselB = b as IMusselLineResource;
        if (musselA.growing_cycle) {
          return musselB.growing_cycle
            ? musselA.growing_cycle.inventories_sum.reduce(
                (p, c) => p + c.quantity,
                0,
              ) -
                musselB.growing_cycle.inventories_sum.reduce(
                  (p, c) => p + c.quantity,
                  0,
                )
            : 1;
        } else {
          return musselB.growing_cycle ? 1 : 0;
        }
      },
    },
    {
      title: transUnit(lang, 'fill_percent', farmData.structure),
      key: 'fill_percent',
      render: (d: ILineResource) => (
        <div>{!d.fill_percent ? '-' : `${d.fill_percent} %`}</div>
      ),
      sorter: (a: ILineResource, b: ILineResource) =>
        (a.fill_percent ?? 0) - (b.fill_percent ?? 0),
    },
    {
      title: getLineColumnLabel('est_amount', lang),
      key: 'est_amount',
      render: (d: ILineResource) => {
        const lss = d.growing_cycle?.last_assessment;
        return displayValueUnit(
          lss?.estimated_amount,
          accountSetting?.display_unit,
          d.crop_type === 'MUSSEL'
            ? 'weight_kg'
            : d.crop_type === 'OYSTER'
              ? 'dz'
              : undefined,
          '-',
        );
      },
      sorter: (a: ILineResource, b: ILineResource) => {
        return (
          (a.growing_cycle?.last_assessment?.estimated_amount ?? 0) -
          (b.growing_cycle?.last_assessment?.estimated_amount ?? 0)
        );
      },
    },
    {
      title: getLineColumnLabel('photo', lang),
      key: 'photo',
      render: (x: ILineResource) => (
        <span onClick={e => e.stopPropagation()}>
          <PhotosViewButton lineID={x.id} label={translate(lang, 'View')} />
        </span>
      ),
    },
    {
      title: getLineColumnLabel('comment', lang),
      key: 'comment',
      render: (x: ILineResource) => (
        <div style={{ maxWidth: '300px' }}>
          {x.growing_cycle?.last_assessment?.comment}
        </div>
      ),
    },
    {
      title: '',
      key: 'more',
      render: (d: ILineResource) => {
        let menuItems: IMenu[] = [
          {
            label: translate(lang, 'Edit'),
            onClick: () => setEditLine(d),
          },
        ];
        if (farmData.structure !== 'RAFT') {
          menuItems = [
            ...menuItems,
            {
              label: translate(lang, 'Location'),
              onClick: () => onEditLineLocation(d),
            },
          ];
        }
        if (isAdmin) {
          if (d.is_active) {
            menuItems = [
              ...menuItems,
              {
                label: translate(lang, 'Archive'),
                onClick: () => archiveLine(d),
              },
            ];
          }
          menuItems = [
            ...menuItems,
            {
              label: translate(lang, 'Delete'),
              onClick: () => deleteLine(d),
              disabled: !isAdmin,
            },
          ];
        }
        return <div>{isEditable && <DotsMenu items={menuItems} />}</div>;
      },
    },
  ]
    .filter(x => !hiddenColumns?.includes(x.key))
    .map(x => ({
      ...x,
      title: translate(lang, x.title),
    }));

  const columns =
    columnsOrder !== undefined && columnsOrder.length > 0
      ? [
          ...columnsOrder
            .map(orderKey => tempColumns.find(col => col.title === orderKey))
            .filter((col): col is (typeof tempColumns)[number] => Boolean(col)),
          ...tempColumns.filter(col => !columnsOrder.includes(col.title)),
        ]
      : tempColumns;

  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);

  const handleTopScroll = () => {
    if (bottomScrollRef.current && topScrollRef.current) {
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };
  const handleBottomScroll = () => {
    if (topScrollRef.current && bottomScrollRef.current) {
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
  };

  useEffect(() => {
    const scrollTop = window.document.getElementById('lines-table-scroll-top');
    const tables = window.document.getElementsByClassName('table--lines');
    if (scrollTop && tables.length > 0) {
      scrollTop.style.width = `${tables[0].clientWidth}px`;
    }
  }, [linesData]);

  return (
    <div>
      <div
        ref={topScrollRef}
        onScroll={handleTopScroll}
        style={{
          overflowX: 'auto',
          height: '20px',
          marginTop: '8px',
        }}
      >
        <div id='lines-table-scroll-top' style={{ width: '100%' }} />
      </div>
      <div
        ref={bottomScrollRef}
        onScroll={handleBottomScroll}
        style={{ overflowX: 'auto' }}
      >
        <Table
          rowKey={'id'}
          className={`table table--lines ${isChild ? 'table--is__child' : ''}`}
          pagination={false}
          columns={columns}
          dataSource={linesData}
          onRow={dataRow => ({
            onClick: () =>
              dataRow.is_active
                ? redirectToLine(dataRow.farm_id, dataRow.id)
                : undefined,
          })}
          onChange={changeTable}
          scroll={{ x: 'max-content' }}
        />
      </div>
      {editLine && (
        <LineFormModal
          visible={true}
          lineData={editLine}
          onClose={() => setEditLine(undefined)}
          farmData={farmData}
        />
      )}
    </div>
  );
};

const lineFilterOptions = (lang: any) => [
  { id: 'all', value: 'all', label: translate(lang, 'All') },
  {
    id: 'empty_lines',
    value: 'empty_lines',
    label: translate(lang, 'Empty Lines'),
  },
  {
    id: 'ready_harvest',
    value: 'ready_harvest',
    label: translate(lang, 'Ready To Harvest'),
  },
  {
    id: 'recently_seeded',
    value: 'recently_seeded',
    label: translate(lang, 'Recently Seeded'),
  },
];

interface IFilter {
  spatSource?: string;
  tagName?: string;
  sizeIndexes: number[];
  taxonomyGroupIds?: number[];
  lineName?: string;
  status: string;
}

interface Props {
  farmData: IFarmResource;
  isChild?: boolean;
  isActive?: boolean;
}

const LinesTable = ({ farmData, isChild, isActive = true }: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const lineTags = useSelector(selectAllTags);

  const [filter, setFilter] = useState<IFilter>({
    sizeIndexes: [],
    status: 'all',
  });
  const [editLineLocation, setEditLineLocation] = useState<ILineResource>();
  const [disabled, setDisabled] = useState(false);

  const updateLineLocation = async (points: any, goNext: boolean) => {
    if (disabled || !editLineLocation) return;

    setDisabled(true);
    const response = await sendSingleRequest(
      { line_id: editLineLocation.id, points },
      'PUT',
      `api/farm/line/lines/${editLineLocation.id}`,
      true,
    );
    if (response.status) {
      await dispatch(loadFarmsData());
      setDisabled(false);
      if (goNext) {
        dispatch(
          showFeedback({
            isMessageModal: true,
            message: translate(lang, '_line_updated_ok'),
            type: 'success',
          }),
        );
        const i = farmData.lines.findIndex(x => x.id === editLineLocation.id);
        const nl = farmData.lines[(i + 1) % farmData.lines.length];
        setEditLineLocation(nl);
      } else {
        dispatch(
          showFeedback({
            isMessage: true,
            message: translate(lang, '_line_updated_ok'),
            type: 'success',
          }),
        );
        setEditLineLocation(undefined);
      }
    } else {
      setDisabled(false);
      window.alert(response.data?.message ?? 'Failed to update the line');
    }
  };

  const linesData = useMemo(() => {
    let result = [...farmData.lines];
    if (farmData.is_active) {
      result = result.filter(x => x.is_active === isActive);
    }
    if (filter.status === 'empty_lines') {
      result = result.filter(x => !x.growing_cycle);
    } else if (filter.status === 'ready_harvest') {
      let res = result.filter(
        x => x.growing_cycle && !!x.growing_cycle.ready_harvest,
      );
      if (res)
        res.sort((a, b) =>
          (a.growing_cycle?.ready_harvest ?? 0) >
          (b.growing_cycle?.ready_harvest ?? 0)
            ? -1
            : 1,
        );
      result = res;
    } else if (filter.status === 'recently_seeded') {
      let res = result.filter(x => !!x.growing_cycle);
      if (res)
        res.sort((a, b) =>
          (a.growing_cycle?.main_seed.planned_date_seed ?? 0) >
          (b.growing_cycle?.main_seed.planned_date_seed ?? 0)
            ? -1
            : 1,
        );
      result = res;
    }
    if (filter.lineName) {
      result = result.filter(x =>
        x.line_name
          .toLowerCase()
          .includes(filter.lineName?.toLowerCase() ?? ''),
      );
    }
    result = result.filter(x => checkLineFilter(x, filter, lineTags));
    return result;
  }, [farmData, filter, lineTags, isActive]);

  return (
    <div className='w-100'>
      {!isChild && (
        <div className='lines-filter-bar'>
          <div style={{ minWidth: '200px' }}>
            <Dropdown
              label=''
              value={filter.status}
              options={lineFilterOptions(lang)}
              onChange={val => setFilter({ ...filter, status: val })}
              allowClear={true}
            />
          </div>
          <div className='ml-12 mr-12' style={{ width: '150px' }}>
            <Input
              type='text'
              label={translate(lang, 'Search by Line name')}
              value={filter.lineName ?? ''}
              placeholder=''
              onChange={e =>
                setFilter({
                  ...filter,
                  lineName:
                    e.target.value.length <= 0 ? undefined : e.target.value,
                })
              }
            />
          </div>
          <LinesFilterView
            filter={filter}
            onChange={v => setFilter({ ...filter, ...v })}
          />
        </div>
      )}
      <UnitedLinesTable
        farmData={farmData}
        linesData={linesData}
        isChild={isChild}
        onEditLineLocation={line => setEditLineLocation(line)}
      />
      {!!editLineLocation && (
        <LineLocationModal
          visible={true}
          lineData={editLineLocation}
          farmID={editLineLocation.farm_id}
          points={editLineLocation.points ?? undefined}
          onConfirm={points => updateLineLocation(points, false)}
          onConfirmNext={points => updateLineLocation(points, true)}
          onCancel={() => setEditLineLocation(undefined)}
        />
      )}
    </div>
  );
};

export default LinesTable;
